import { $patchStyleText } from '@lexical/selection';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  FORMAT_TEXT_COMMAND,
  INSERT_PARAGRAPH_COMMAND,
  KEY_ENTER_COMMAND,
  LexicalEditor,
  createCommand,
} from 'lexical';
import * as BrandDefinition from 'modules/BrandDefinition';
import * as Constants from 'const';
import * as Models from 'models';
import { toPx } from 'utils/toPx';
import { FontPluginStyle, getStyleObjectForBrandColor, getStyleObjectForBrandFont } from './style';
import { $getCharStyle, $patchFontStyle, $patchFontWeight } from './utils';

export const FONT_COMMAND = {
  BRAND_COLOR: createCommand<Models.BrandColorMap | undefined>(),
  BRAND_FONT: createCommand<{
    brandFont: Models.BrandFontMap;
    characterStyle?: Models.CharacterStyleMap;
  }>(),
  SIZE: createCommand<number>(),
  CHAR_STYLE: createCommand<Constants.CharStyle>(),
} as const;

export function registerCommands(
  editor: LexicalEditor,
  fonts: Models.BrandFontsList,
  colors: Models.BrandColorsList,
  defaultBrandColorRef: BrandDefinition.BrandColorRef | undefined,
): () => void {

  return mergeRegister(
    editor.registerCommand<KeyboardEvent | null>(
      KEY_ENTER_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {

          return false;
        }
        if (event !== null) {
          event.preventDefault();
        }

        return editor.dispatchCommand(INSERT_PARAGRAPH_COMMAND, undefined);
      },
      COMMAND_PRIORITY_HIGH,
    ),

    editor.registerCommand(
      FONT_COMMAND.BRAND_COLOR,
      (color) => {
        const selection = $getSelection();
        if (!selection || !$isRangeSelection(selection)) {
          return false;
        }
        $patchStyleText(
          selection,
          color ? getStyleObjectForBrandColor(color) : getStyleObjectForBrandColor(colors, defaultBrandColorRef),
        );

        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    ),

    editor.registerCommand(
      FONT_COMMAND.BRAND_FONT,
      ({ brandFont, characterStyle }) => {
        const selection = $getSelection();
        if (!selection || !$isRangeSelection(selection)) {
          return false;
        }
        const fontRef = { fontName: brandFont.get('name'), characterStyleName: characterStyle?.get('name') };
        $patchStyleText(selection, getStyleObjectForBrandFont(fonts, fontRef));

        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    ),

    editor.registerCommand(
      FONT_COMMAND.SIZE,
      (size) => {
        const selection = $getSelection();
        if (selection && $isRangeSelection(selection)) {
          $patchStyleText(selection, {
            [FontPluginStyle.FONT_SIZE]: toPx(size) ?? null,
          });
        }

        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    ),

    editor.registerCommand(
      FONT_COMMAND.CHAR_STYLE,
      (style) => {
        if (style === Constants.CharStyle.UNDERLINE) {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');

          return true;
        }

        const selection = $getSelection();
        if (!selection && !$isRangeSelection(selection)) {
          return false;
        }

        const currentStyles = $getCharStyle();
        switch (style) {
          case Constants.CharStyle.BOLD:
            $patchFontWeight(selection, !currentStyles.includes(Constants.CharStyle.BOLD));
            break;
          case Constants.CharStyle.ITALIC:
            $patchFontStyle(selection, currentStyles.includes(Constants.CharStyle.ITALIC));
            break;
          default:
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, no-console
            console.error(`unknown style ${style}`);
        }

        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    ),

    editor.registerCommand(
      FORMAT_TEXT_COMMAND,
      (formatType) => {
        if (formatType === 'bold') {
          editor.dispatchCommand(FONT_COMMAND.CHAR_STYLE, Constants.CharStyle.BOLD);

          return true;
        }
        if (formatType === 'italic') {
          editor.dispatchCommand(FONT_COMMAND.CHAR_STYLE, Constants.CharStyle.ITALIC);

          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    ),
  );
}
